import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import useModuleAccess from '../hooks/useModuleAccess'

const API_URL = process.env.REACT_APP_API_URL

const SubcategoryStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
)

const SUBCATEGORY_SORT_FIELDS = [
  'subcategoryId',
  'subcategoryName',
  'createdAt'
]

const SUBCATEGORY_SORT_COLUMN_MAP = Object.fromEntries(
  SUBCATEGORY_SORT_FIELDS.map((field) => [field, field])
)

const DEFAULT_SORT_BY = 'createdAt'
const DEFAULT_SORT_ORDER = 'desc'

function normalizeSortBy(value) {
  const field = String(value || '').trim()
  return SUBCATEGORY_SORT_FIELDS.includes(field) ? field : DEFAULT_SORT_BY
}

function normalizeSortOrder(value) {
  const order = String(value || '').trim().toLowerCase()
  return order === 'asc' ? 'asc' : 'desc'
}

function parseApiResponse(raw) {
  let data = raw
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return raw
    }
  }
  if (data?.body != null) {
    if (typeof data.body === 'string') {
      try {
        return JSON.parse(data.body)
      } catch {
        return data.body
      }
    }
    return data.body
  }
  return data
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  if (
    obj.data &&
    typeof obj.data === 'object' &&
    typeof obj.data.data === 'string'
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) }
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const success = responseData.success
  return success === true || success === 'true' || success === 1 || success === '1'
}

function extractSubcategoriesPayload(responseData) {
  const root = responseData
  let dataBlock = root?.data != null ? root.data : root

  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') {
    return { subcategories: [], pagination: {}, sortBy: null, sortOrder: null }
  }

  if (Array.isArray(dataBlock)) {
    return {
      subcategories: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === 'object'
          ? root.pagination
          : {},
      sortBy: root.sortBy || null,
      sortOrder: root.sortOrder || null
    }
  }

  let list =
    dataBlock.subcategories ||
    dataBlock.subcategoryList ||
    dataBlock.items ||
    root.subcategories

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === 'string'
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data
    if (inner && typeof inner === 'object') {
      list = inner.subcategories || inner.subcategoryList || inner.items
    }
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {}

  if (typeof list === 'string') {
    list = parseJsonIfString(list)
  }

  const total =
    typeof pagination.total === 'number'
      ? pagination.total
      : typeof pagination.totalCount === 'number'
        ? pagination.totalCount
        : typeof dataBlock.totalCount === 'number'
          ? dataBlock.totalCount
          : typeof dataBlock.total === 'number'
            ? dataBlock.total
            : undefined

  return {
    subcategories: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === 'object' && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
    sortBy: dataBlock.sortBy || root.sortBy || null,
    sortOrder: dataBlock.sortOrder || root.sortOrder || null
  }
}

function extractSubcategoryCountsPayload(responseData) {
  const root = responseData || {}
  let dataBlock = root.data != null ? root.data : root

  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }

  if (
    dataBlock &&
    typeof dataBlock === 'object' &&
    dataBlock.data != null
  ) {
    dataBlock =
      typeof dataBlock.data === 'string'
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data
  }

  const counts =
    dataBlock && typeof dataBlock === 'object' && !Array.isArray(dataBlock)
      ? dataBlock
      : {}

  const toCount = (value) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return {
    subcategoryCount: toCount(counts.subcategoryCount),
    totalActiveCount: toCount(counts.totalActiveCount)
  }
}

function extractCategoriesList(responseData) {
  const root = responseData
  let dataBlock = root?.data != null ? root.data : root

  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') {
    return []
  }

  let list =
    dataBlock.categories ||
    dataBlock.categoryList ||
    dataBlock.items ||
    root.categories

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === 'string'
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data
    if (inner && typeof inner === 'object') {
      list = inner.categories || inner.categoryList || inner.items
    }
  }

  if (typeof list === 'string') {
    list = parseJsonIfString(list)
  }

  return Array.isArray(list) ? list : []
}

function subcategoryUserName(user) {
  if (!user) return '-'
  if (typeof user === 'string') return user.trim() || '-'
  return user.name || user.userName || '-'
}

function formatSubcategoryDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

function extractSubcategoryDetail(responseData) {
  const root = responseData
  if (!root || typeof root !== 'object') return null
  const data = root.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data
  }
  return null
}

const SubcategoryDetailCard = ({ label, value, icon }) => (
  <div
    className="h-100"
    style={{
      backgroundColor: '#f8fafb',
      border: '1px solid #e3e8ee',
      borderRadius: '8px',
      padding: '14px 16px'
    }}
  >
    <div
      className="d-flex align-items-center mb-2"
      style={{ color: '#6c757d', fontSize: '12px', fontWeight: 600 }}
    >
      {icon ? <i className={`${icon} mr-2`} style={{ color: '#1199B2' }} /> : null}
      {label}
    </div>
    <div style={{ color: '#212529', fontSize: '14px', fontWeight: 600, wordBreak: 'break-word' }}>
      {value || '-'}
    </div>
  </div>
)

function subcategoryLabel(subcategory) {
  const name = String(subcategory?.subcategoryName || '').trim()
  const id = String(subcategory?.subcategoryId || '').trim()
  if (name && id) return `${name} (${id})`
  return name || id || '-'
}

function subcategoryCategoryLabel(subcategory) {
  if (subcategory?.categoryName) return subcategory.categoryName
  if (subcategory?.category?.categoryName) return subcategory.category.categoryName
  if (subcategory?.parentCategoryName) return subcategory.parentCategoryName
  if (subcategory?.categoryId) return subcategory.categoryId
  if (subcategory?.category?.categoryId) return subcategory.category.categoryId
  return '-'
}

function subcategoryParentCategoryId(subcategory) {
  if (subcategory?.categoryId) return String(subcategory.categoryId)
  if (subcategory?.category?.categoryId) return String(subcategory.category.categoryId)
  return ''
}

function renderSubcategoryStatus(status) {
  const value = String(status || '').trim()
  if (!value) return '-'
  const normalized = value.toLowerCase()
  const badgeClass =
    normalized === 'active'
      ? 'badge-success'
      : normalized === 'inactive'
        ? 'badge-danger'
        : 'badge-secondary'
  return (
    <span className={`badge ${badgeClass}`} style={{ fontSize: '90%', fontWeight: 550 }}>
      {value}
    </span>
  )
}

const Subcategory = () => {
  const { canCreate, canView, canUpdate, canDelete } = useModuleAccess(
    'subcategory_management'
  )
  const savedFilters = JSON.parse(sessionStorage.getItem('subcategoryFilters') || '{}')

  const [subcategories, setSubcategories] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery || '')
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || ''
  )
  const [filterCategoryId, setFilterCategoryId] = useState(
    savedFilters.filterCategoryId || ''
  )
  const [activeFilterCategoryId, setActiveFilterCategoryId] = useState(
    savedFilters.activeFilterCategoryId || ''
  )
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1)
  const [itemsPerPage, setItemsPerPage] = useState(savedFilters.itemsPerPage || 25)
  const [deleteSubcategoryId, setDeleteSubcategoryId] = useState(null)
  const [deleteSubcategoryLabel, setDeleteSubcategoryLabel] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortBy, setSortBy] = useState(
    normalizeSortBy(savedFilters.sortBy || DEFAULT_SORT_BY)
  )
  const [sortOrder, setSortOrder] = useState(
    normalizeSortOrder(savedFilters.sortOrder || DEFAULT_SORT_ORDER)
  )
  const [isSubcategoryCountLoading, setIsSubcategoryCountLoading] = useState(true)
  const [subcategoryCount, setSubcategoryCount] = useState({
    subcategoryCount: 0,
    totalActiveCount: 0
  })
  const [nextSubcategoryId, setNextSubcategoryId] = useState('')
  const [addSubcategoryName, setAddSubcategoryName] = useState('')
  const [addCategoryId, setAddCategoryId] = useState('')
  const [isLoadingNextSubcategoryId, setIsLoadingNextSubcategoryId] = useState(false)
  const [isCreatingSubcategory, setIsCreatingSubcategory] = useState(false)
  const [viewSubcategory, setViewSubcategory] = useState(null)
  const [isLoadingViewSubcategory, setIsLoadingViewSubcategory] = useState(false)
  const [editSubcategory, setEditSubcategory] = useState(null)
  const [editSubcategoryName, setEditSubcategoryName] = useState('')
  const [editCategoryId, setEditCategoryId] = useState('')
  const [isLoadingEditSubcategory, setIsLoadingEditSubcategory] = useState(false)
  const [isUpdatingSubcategory, setIsUpdatingSubcategory] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState([])
  const [isLoadingCategoryOptions, setIsLoadingCategoryOptions] = useState(false)

  const fetchCategoryOptions = async () => {
    setIsLoadingCategoryOptions(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-all-categories',
        page: 1,
        limit: 500,
        sortBy: 'categoryName',
        sortOrder: 'asc'
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      let list = responseData?.data?.categories

      if (!Array.isArray(list)) {
        list = extractCategoriesList(responseData)
      }

      if (isApiSuccess(responseData) && Array.isArray(list)) {
        setCategoryOptions(list)
      } else {
        setCategoryOptions([])
      }
    } catch (error) {
      console.error('Fetch category options error:', error)
      setCategoryOptions([])
    } finally {
      setIsLoadingCategoryOptions(false)
    }
  }

  const fetchNextSubcategoryId = async (categoryId) => {
    const trimmedCategoryId = String(categoryId || '').trim()
    if (!trimmedCategoryId) {
      setNextSubcategoryId('')
      setIsLoadingNextSubcategoryId(false)
      return
    }

    setIsLoadingNextSubcategoryId(true)
    setNextSubcategoryId('')

    try {
      const response = await axios.post(API_URL, {
        action: 'get-next-subcategory-id',
        categoryId: trimmedCategoryId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData) && responseData.data?.nextSubcategoryId) {
        setNextSubcategoryId(String(responseData.data.nextSubcategoryId))
      } else {
        toast.error(responseData?.message || 'Failed to fetch subcategory ID', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Fetch next subcategory ID error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching subcategory ID',
        { autoClose: 2000 }
      )
    } finally {
      setIsLoadingNextSubcategoryId(false)
    }
  }

  const handleAddCategoryChange = (e) => {
    const categoryId = e.target.value
    setAddCategoryId(categoryId)
    setNextSubcategoryId('')
    if (categoryId) {
      fetchNextSubcategoryId(categoryId)
    }
  }

  const handleOpenAddSubcategoryModal = () => {
    setAddSubcategoryName('')
    setAddCategoryId('')
    setNextSubcategoryId('')
    setIsLoadingNextSubcategoryId(false)
    if (window.$) {
      window.$('#modaladdsubcategory').modal('show')
    }
    fetchCategoryOptions()
  }

  const handleCloseAddSubcategoryModal = () => {
    if (window.$) {
      window.$('#modaladdsubcategory').modal('hide')
    }
    setAddSubcategoryName('')
    setAddCategoryId('')
    setNextSubcategoryId('')
    setIsLoadingNextSubcategoryId(false)
  }

  const handleCreateSubcategory = async () => {
    if (!addCategoryId) {
      toast.error('Category is required', { autoClose: 2000 })
      return
    }

    if (!addSubcategoryName.trim()) {
      toast.error('Subcategory name is required', { autoClose: 2000 })
      return
    }

    const createdBy = localStorage.getItem('id')
    if (!createdBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsCreatingSubcategory(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'create-subcategory',
        categoryId: addCategoryId,
        subcategoryName: addSubcategoryName.trim().toUpperCase(),
        createdBy
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Subcategory created successfully', {
          autoClose: 2000
        })
        handleCloseAddSubcategoryModal()
        if (currentPage !== 1) {
          setCurrentPage(1)
        } else {
          fetchSubcategories()
        }
        fetchSubcategoryCounts()
      } else {
        toast.error(responseData?.message || 'Failed to create subcategory', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Create subcategory error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while creating subcategory',
        { autoClose: 2000 }
      )
    } finally {
      setIsCreatingSubcategory(false)
    }
  }

  const handleCloseViewSubcategoryModal = () => {
    if (window.$) {
      window.$('#modalviewsubcategory').modal('hide')
    }
    setViewSubcategory(null)
  }

  const fetchSubcategoryById = async (subcategoryId) => {
    setIsLoadingViewSubcategory(true)
    setViewSubcategory(null)

    try {
      const response = await axios.post(API_URL, {
        action: 'get-subcategory-by-id',
        id: subcategoryId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const subcategory = extractSubcategoryDetail(responseData)

      if (isApiSuccess(responseData) && subcategory) {
        setViewSubcategory(subcategory)
      } else {
        toast.error(responseData?.message || 'Failed to fetch subcategory details', {
          autoClose: 2000
        })
        handleCloseViewSubcategoryModal()
      }
    } catch (error) {
      console.error('Fetch subcategory error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching subcategory details',
        { autoClose: 2000 }
      )
      handleCloseViewSubcategoryModal()
    } finally {
      setIsLoadingViewSubcategory(false)
    }
  }

  const handleViewClick = (subcategory) => {
    const subcategoryId = subcategory?._id || subcategory?.id
    if (!subcategoryId) {
      toast.error('Subcategory ID is missing', { autoClose: 2000 })
      return
    }

    if (window.$) {
      window.$('#modalviewsubcategory').modal('show')
    }
    fetchSubcategoryById(subcategoryId)
  }

  const handleCloseEditSubcategoryModal = () => {
    if (window.$) {
      window.$('#modaleditsubcategory').modal('hide')
    }
    setEditSubcategory(null)
    setEditSubcategoryName('')
    setEditCategoryId('')
  }

  const fetchSubcategoryForEdit = async (subcategoryId) => {
    setIsLoadingEditSubcategory(true)
    setEditSubcategory(null)
    setEditSubcategoryName('')
    setEditCategoryId('')

    try {
      const response = await axios.post(API_URL, {
        action: 'get-subcategory-by-id',
        id: subcategoryId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const subcategory = extractSubcategoryDetail(responseData)

      if (isApiSuccess(responseData) && subcategory) {
        setEditSubcategory(subcategory)
        setEditSubcategoryName(String(subcategory.subcategoryName || '').toUpperCase())
        setEditCategoryId(subcategoryParentCategoryId(subcategory))
      } else {
        toast.error(responseData?.message || 'Failed to fetch subcategory details', {
          autoClose: 2000
        })
        handleCloseEditSubcategoryModal()
      }
    } catch (error) {
      console.error('Fetch subcategory for edit error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while fetching subcategory details',
        { autoClose: 2000 }
      )
      handleCloseEditSubcategoryModal()
    } finally {
      setIsLoadingEditSubcategory(false)
    }
  }

  const handleEditClick = (subcategory) => {
    const subcategoryId = subcategory?._id || subcategory?.id
    if (!subcategoryId) {
      toast.error('Subcategory ID is missing', { autoClose: 2000 })
      return
    }

    if (window.$) {
      window.$('#modaleditsubcategory').modal('show')
    }
    fetchCategoryOptions()
    fetchSubcategoryForEdit(subcategoryId)
  }

  const handleUpdateSubcategory = async () => {
    if (!editSubcategoryName.trim()) {
      toast.error('Subcategory name is required', { autoClose: 2000 })
      return
    }

    if (!editCategoryId) {
      toast.error('Category is required', { autoClose: 2000 })
      return
    }

    const id = editSubcategory?._id || editSubcategory?.id
    if (!id) {
      toast.error('Subcategory ID is missing', { autoClose: 2000 })
      return
    }

    const updatedBy = localStorage.getItem('id')
    if (!updatedBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsUpdatingSubcategory(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'update-subcategory',
        id,
        updatedBy,
        subcategoryName: editSubcategoryName.trim().toUpperCase(),
        categoryId: editCategoryId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Subcategory updated successfully', {
          autoClose: 2000
        })
        handleCloseEditSubcategoryModal()
        fetchSubcategories()
        fetchSubcategoryCounts()
      } else {
        toast.error(responseData?.message || 'Failed to update subcategory', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Update subcategory error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || 'An error occurred while updating subcategory',
        { autoClose: 2000 }
      )
    } finally {
      setIsUpdatingSubcategory(false)
    }
  }

  const fetchSubcategoryCounts = useCallback(async () => {
    setIsSubcategoryCountLoading(true)

    try {
      const response = await axios.post(API_URL, {
        action: 'subcategory-counts'
      })

      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        setSubcategoryCount(extractSubcategoryCountsPayload(responseData))
      } else {
        setSubcategoryCount({
          subcategoryCount: 0,
          totalActiveCount: 0
        })
      }
    } catch (error) {
      console.error('Subcategory counts error:', error)
    } finally {
      setIsSubcategoryCountLoading(false)
    }
  }, [])

  const fetchSubcategories = useCallback(async () => {
    setIsLoading(true)
    try {
      const payload = {
        action: 'get-all-subcategories',
        page: currentPage,
        limit: itemsPerPage,
        sortBy: normalizeSortBy(sortBy),
        sortOrder: normalizeSortOrder(sortOrder)
      }

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : ''
      if (trimmedSearch) {
        payload.search = trimmedSearch
      }

      if (activeFilterCategoryId) {
        payload.categoryId = activeFilterCategoryId
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const {
        subcategories: list,
        pagination,
        sortBy: responseSortBy,
        sortOrder: responseSortOrder
      } = extractSubcategoriesPayload(responseData)

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0)

      if (ok) {
        setSubcategories(list)
        const total =
          typeof pagination.total === 'number'
            ? pagination.total
            : typeof pagination.totalCount === 'number'
              ? pagination.totalCount
              : list.length
        setTotalCount(total)

        if (responseSortBy && SUBCATEGORY_SORT_FIELDS.includes(responseSortBy)) {
          setSortBy(responseSortBy)
        }
        if (responseSortOrder === 'asc' || responseSortOrder === 'desc') {
          setSortOrder(responseSortOrder)
        }
      } else {
        toast.error(responseData?.message || 'Failed to fetch subcategories', {
          autoClose: 2000
        })
        setSubcategories([])
        setTotalCount(0)
      }
    } catch (error) {
      console.error('Fetch subcategories error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      const msg =
        typeof errData === 'object' && errData?.message
          ? errData.message
          : 'An error occurred while fetching subcategories'
      toast.error(msg, { autoClose: 2000 })
      setSubcategories([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    activeFilterCategoryId,
    sortBy,
    sortOrder
  ])

  useEffect(() => {
    fetchSubcategories()
  }, [fetchSubcategories])

  useEffect(() => {
    fetchSubcategoryCounts()
  }, [fetchSubcategoryCounts])

  useEffect(() => {
    fetchCategoryOptions()
  }, [])

  useEffect(() => {
    sessionStorage.setItem(
      'subcategoryFilters',
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        filterCategoryId,
        activeFilterCategoryId,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder
      })
    )
  }, [
    searchQuery,
    activeSearchQuery,
    filterCategoryId,
    activeFilterCategoryId,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder
  ])

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1)
  const startItem = subcategories.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const endItem = Math.min(currentPage * itemsPerPage, totalCount)
  const hasActiveFilters = Boolean(activeSearchQuery || activeFilterCategoryId)

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
  }

  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 7
    const tp = totalPages

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i)
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i)
      pages.push('ellipsis')
      pages.push(tp)
    } else if (currentPage >= tp - 3) {
      pages.push(1)
      pages.push('ellipsis')
      for (let i = tp - 4; i <= tp; i++) pages.push(i)
    } else {
      pages.push(1)
      pages.push('ellipsis')
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
      pages.push('ellipsis')
      pages.push(tp)
    }
    return pages
  }

  const handleClearAll = () => {
    setSearchQuery('')
    setActiveSearchQuery('')
    setFilterCategoryId('')
    setActiveFilterCategoryId('')
    if (currentPage !== 1) setCurrentPage(1)
  }

  const handleSearchChange = (e) => {
    const newValue = e.target.value
    setSearchQuery(newValue)
    const trimmedValue = newValue ? newValue.trim() : ''
    if (trimmedValue === '' && activeSearchQuery !== '') {
      setActiveSearchQuery('')
      if (currentPage !== 1) setCurrentPage(1)
    }
  }

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : ''
    if (trimmedQuery === '') {
      setActiveSearchQuery('')
      if (currentPage !== 1) setCurrentPage(1)
    } else {
      setActiveSearchQuery(trimmedQuery)
      if (currentPage !== 1) setCurrentPage(1)
    }
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchTrigger()
    }
  }

  const handleCategoryFilterChange = (e) => {
    const value = e.target.value
    setFilterCategoryId(value)
    setActiveFilterCategoryId(value)
    if (currentPage !== 1) setCurrentPage(1)
  }

  const handleSort = (column) => {
    const apiField = SUBCATEGORY_SORT_COLUMN_MAP[column]
    if (!apiField || !SUBCATEGORY_SORT_FIELDS.includes(apiField)) return

    if (sortBy === apiField) {
      if (sortOrder === 'asc') {
        setSortOrder('desc')
      } else {
        setSortBy(DEFAULT_SORT_BY)
        setSortOrder(DEFAULT_SORT_ORDER)
      }
    } else {
      setSortBy(apiField)
      setSortOrder('asc')
    }

    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }

  const getSortIcon = (column) => {
    const apiField = SUBCATEGORY_SORT_COLUMN_MAP[column]
    if (!apiField) return null

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: '0.8em', flexShrink: 0 }}
        />
      )
    }

    if (sortOrder === 'asc') {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: '0.8em', flexShrink: 0 }}
        />
      )
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: '0.8em', flexShrink: 0 }}
      />
    )
  }

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        ...extraStyle
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  )

  const handleDeleteClick = (subcategory) => {
    const subcategoryId = subcategory?._id || subcategory?.id
    if (!subcategoryId) {
      toast.error('Subcategory ID is missing', { autoClose: 2000 })
      return
    }
    setDeleteSubcategoryId(subcategoryId)
    setDeleteSubcategoryLabel(
      String(subcategory?.subcategoryName || '').trim() || subcategoryLabel(subcategory)
    )
  }

  const handleDelete = async () => {
    if (!deleteSubcategoryId) return

    const updatedBy = localStorage.getItem('id')
    if (!updatedBy) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setIsDeleting(true)
    try {
      const response = await axios.post(API_URL, {
        action: 'delete-subcategory',
        id: deleteSubcategoryId,
        updatedBy
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(responseData?.message || 'Subcategory deleted successfully', {
          autoClose: 2000
        })
        if (window.$) {
          window.$('#modaldeletesubcategory').modal('hide')
        }
        setDeleteSubcategoryId(null)
        setDeleteSubcategoryLabel('')
        if (subcategories.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1)
        } else {
          fetchSubcategories()
        }
        fetchSubcategoryCounts()
      } else {
        toast.error(responseData?.message || 'Failed to delete subcategory', {
          autoClose: 2000
        })
      }
    } catch (error) {
      console.error('Delete subcategory error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(errData?.message || 'An error occurred while deleting subcategory', {
        autoClose: 2000
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const renderCategorySelectOptions = () =>
    categoryOptions.map((category) => {
      const displayId = category.categoryId || ''
      const name = category.categoryName || '-'
      return (
        <option key={displayId || category._id || category.id} value={displayId}>
          {displayId} - {name}
        </option>
      )
    })

  const renderCategoryFilterOptions = () =>
    categoryOptions.map((category) => {
      const displayId = category.categoryId || ''
      const name = category.categoryName || '-'
      return (
        <option key={displayId || category._id || category.id} value={displayId}>
          {displayId} - {name}
        </option>
      )
    })

  const renderEditCategorySelectOptions = () =>
    categoryOptions.map((category) => {
      const displayId = category.categoryId || ''
      const name = category.categoryName || '-'
      return (
        <option key={displayId || category._id || category.id} value={displayId}>
          {displayId} - {name}
        </option>
      )
    })

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Subcategory</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <button
                  type="button"
                  className="btn btn-blue"
                  onClick={handleOpenAddSubcategoryModal}
                >
                  Add Subcategory
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isSubcategoryCountLoading ? (
                  <>
                    <SubcategoryStatCardSkeleton />
                    <SubcategoryStatCardSkeleton />
                  </>
                ) : (
                  <>
                    <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: '#1F618D',
                              fontSize: '13px',
                              fontWeight: '600',
                              marginBottom: '8px'
                            }}
                          >
                            Total Subcategories
                          </div>
                          <div
                            className="stat-value"
                            style={{
                              color: '#2980B9',
                              fontSize: '22px',
                              fontWeight: '700'
                            }}
                          >
                            {subcategoryCount.subcategoryCount}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: '#196F3D',
                              fontSize: '13px',
                              fontWeight: '600',
                              marginBottom: '8px'
                            }}
                          >
                            Total Active Count
                          </div>
                          <div
                            className="stat-value"
                            style={{
                              color: '#27AE60',
                              fontSize: '22px',
                              fontWeight: '700'
                            }}
                          >
                            {subcategoryCount.totalActiveCount}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-3">
                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: '13px', fontWeight: '500' }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search subcategories..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: 'pointer' }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: '13px', fontWeight: '500' }}
                        >
                          Category
                        </label>
                        <select
                          className="form-control"
                          value={filterCategoryId}
                          onChange={handleCategoryFilterChange}
                          disabled={isLoadingCategoryOptions}
                        >
                          <option value="">All Categories</option>
                          {renderCategoryFilterOptions()}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : subcategories.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no subcategories yet.
                  <br />
                  {canCreate && (
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={handleOpenAddSubcategoryModal}
                      >
                        Add Subcategory
                      </button>
                    </div>
                  )}
                </div>
              ) : subcategories.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No subcategories found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="subcategories"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: 'nowrap',
                              width: '40px',
                              minWidth: '40px'
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader('subcategoryId', 'Subcategory ID', {
                            minWidth: '120px'
                          })}
                          {renderSortableHeader('subcategoryName', 'Subcategory Name', {
                            minWidth: '140px'
                          })}
                          <th style={{ minWidth: '120px' }}>Category</th>
                          <th style={{ minWidth: '90px' }}>Status</th>
                          <th style={{ minWidth: '140px' }}>Created By</th>
                          <th style={{ width: '80px', minWidth: '80px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subcategories.map((subcategory, index) => {
                          const subcategoryId = subcategory._id || subcategory.id
                          return (
                            <tr key={subcategoryId || index}>
                              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {subcategory.subcategoryId || '-'}
                              </td>
                              <td
                                style={{
                                  maxWidth: '180px',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal'
                                }}
                              >
                                {subcategory.subcategoryName || '-'}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {subcategoryCategoryLabel(subcategory)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {renderSubcategoryStatus(subcategory.status)}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {subcategoryUserName(subcategory.createdBy)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: 'nowrap' }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canView && (
                                    <a
                                      href="#modalviewsubcategory"
                                      className="crsr_pntr action-icon-link mr-2"
                                      title="View"
                                      onClick={() => handleViewClick(subcategory)}
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </a>
                                  )}
                                  {canUpdate && (
                                    <a
                                      href="#modaleditsubcategory"
                                      className="crsr_pntr action-icon-link mr-2"
                                      title="Edit"
                                      onClick={() => handleEditClick(subcategory)}
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </a>
                                  )}
                                  {canDelete && (
                                    <a
                                      href="#modaldeletesubcategory"
                                      className="crsr_pntr action-icon-link"
                                      data-toggle="modal"
                                      data-target="#modaldeletesubcategory"
                                      title="Delete"
                                      onClick={() => handleDeleteClick(subcategory)}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && subcategories.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: '13px' }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: '13px' }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: '13px' }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === 'ellipsis' ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? 'active' : ''
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalviewsubcategory"
        tabIndex={-1}
        aria-labelledby="modalviewsubcategoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content" style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}>
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modalviewsubcategoryLabel">
                <i className="fa-solid fa-sitemap mr-2" />
                View Subcategory
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseViewSubcategoryModal}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              {isLoadingViewSubcategory ? (
                <div className="text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                  <p className="text-muted small mt-3 mb-0">Loading subcategory details...</p>
                </div>
              ) : viewSubcategory ? (
                <>
                  <div
                    className="mb-4 p-3"
                    style={{
                      background: 'linear-gradient(135deg, #f0f9fb 0%, #e8f4f8 100%)',
                      border: '1px solid #cfe8ef',
                      borderRadius: '10px'
                    }}
                  >
                    <div className="d-flex align-items-start" style={{ gap: '16px' }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#1199B2',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            marginBottom: '6px'
                          }}
                        >
                          Subcategory Name
                        </div>
                        <div
                          style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            color: '#17324d',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere'
                          }}
                        >
                          {viewSubcategory.subcategoryName || '-'}
                        </div>
                      </div>
                      <div
                        className="d-flex flex-column align-items-end flex-shrink-0"
                        style={{ flex: '0 0 auto', marginLeft: 'auto' }}
                      >
                        <div
                          className="badge badge-light text-dark"
                          style={{ fontSize: '12px', padding: '6px 10px', whiteSpace: 'nowrap' }}
                        >
                          ID: {viewSubcategory.subcategoryId || '-'}
                        </div>
                        <div className="mt-2">{renderSubcategoryStatus(viewSubcategory.status)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-sm-6 mb-3">
                      <SubcategoryDetailCard
                        label="Category"
                        value={subcategoryCategoryLabel(viewSubcategory)}
                        icon="fa-solid fa-layer-group"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <SubcategoryDetailCard
                        label="Created By"
                        value={subcategoryUserName(viewSubcategory.createdBy)}
                        icon="fa-solid fa-user"
                      />
                    </div>
                    <div className="col-sm-6 mb-3">
                      <SubcategoryDetailCard
                        label="Updated By"
                        value={subcategoryUserName(viewSubcategory.updatedBy)}
                        icon="fa-solid fa-user-pen"
                      />
                    </div>
                    <div className="col-sm-6 mb-0 mb-sm-0">
                      <SubcategoryDetailCard
                        label="Created At"
                        value={formatSubcategoryDate(viewSubcategory.createdAt)}
                        icon="fa-regular fa-calendar"
                      />
                    </div>
                    <div className="col-sm-6 mb-0">
                      <SubcategoryDetailCard
                        label="Updated At"
                        value={formatSubcategoryDate(viewSubcategory.updatedAt)}
                        icon="fa-regular fa-clock"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="fa-regular fa-folder-open fa-2x mb-3 d-block" />
                  Subcategory details not available.
                </div>
              )}
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-blue px-4"
                data-dismiss="modal"
                onClick={handleCloseViewSubcategoryModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaladdsubcategory"
        tabIndex={-1}
        aria-labelledby="modaladdsubcategoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content" style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}>
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modaladdsubcategoryLabel">
                <i className="fa-solid fa-circle-plus mr-2" />
                Add Subcategory
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseAddSubcategoryModal}
                disabled={isCreatingSubcategory}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              <div className="form-group mb-3">
                <label
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Category
                </label>
                <select
                  className="form-control"
                  value={addCategoryId}
                  onChange={handleAddCategoryChange}
                  disabled={
                    isCreatingSubcategory ||
                    isLoadingCategoryOptions
                  }
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #cfe8ef',
                    height: '42px',
                    padding: '0 14px',
                    fontSize: '15px',
                    lineHeight: '42px',
                    color: '#17324d',
                    backgroundColor: '#fff'
                  }}
                >
                  <option value="">
                    {isLoadingCategoryOptions ? 'Loading categories...' : 'Select category'}
                  </option>
                  {renderCategorySelectOptions()}
                </select>
              </div>

              {addCategoryId ? (
                <div
                  className="mb-4 p-3"
                  style={{
                    background: 'linear-gradient(135deg, #f0f9fb 0%, #e8f4f8 100%)',
                    border: '1px solid #cfe8ef',
                    borderRadius: '10px'
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#1199B2',
                      textTransform: 'uppercase',
                      letterSpacing: '0.4px',
                      marginBottom: '8px'
                    }}
                  >
                    Subcategory ID
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#17324d' }}>
                    {isLoadingNextSubcategoryId ? (
                      <span className="text-muted" style={{ fontSize: '14px', fontWeight: 500 }}>
                        <PulseLoader color="#1199B2" size={6} className="mr-2" />
                        Loading...
                      </span>
                    ) : (
                      nextSubcategoryId || '-'
                    )}
                  </div>
                </div>
              ) : null}

              <div className="form-group mb-0">
                <label
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    marginBottom: '8px',
                    display: 'block'
                  }}
                >
                  Subcategory Name
                </label>
                <textarea
                  rows={3}
                  className="form-control"
                  placeholder="Enter subcategory name"
                  value={addSubcategoryName}
                  onChange={(e) => setAddSubcategoryName(e.target.value.toUpperCase())}
                  disabled={isCreatingSubcategory}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #cfe8ef',
                    padding: '10px 14px',
                    fontSize: '15px',
                    resize: 'vertical',
                    minHeight: '78px',
                    textTransform: 'uppercase'
                  }}
                />
              </div>
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-secondary mr-2 px-4"
                data-dismiss="modal"
                onClick={handleCloseAddSubcategoryModal}
                disabled={isCreatingSubcategory}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue px-4"
                onClick={handleCreateSubcategory}
                disabled={
                  isCreatingSubcategory ||
                  isLoadingNextSubcategoryId ||
                  !addCategoryId ||
                  !addSubcategoryName.trim()
                }
              >
                {isCreatingSubcategory ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaleditsubcategory"
        tabIndex={-1}
        aria-labelledby="modaleditsubcategoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content" style={{ border: 'none', borderRadius: '10px', overflow: 'hidden' }}>
            <div
              className="modal-header border-0"
              style={{ backgroundColor: '#1199B2', color: '#fff', padding: '16px 20px' }}
            >
              <h5 className="modal-title mb-0" id="modaleditsubcategoryLabel">
                <i className="fa-solid fa-pen-to-square mr-2" />
                Edit Subcategory
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseEditSubcategoryModal}
                disabled={isUpdatingSubcategory}
                style={{ color: '#fff', opacity: 1, textShadow: 'none' }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', backgroundColor: '#fff' }}>
              {isLoadingEditSubcategory ? (
                <div className="text-center py-5">
                  <PulseLoader color="#1199B2" size={10} />
                  <p className="text-muted small mt-3 mb-0">Loading subcategory details...</p>
                </div>
              ) : editSubcategory ? (
                <>
                  <div
                    className="mb-4 p-3"
                    style={{
                      background: 'linear-gradient(135deg, #f0f9fb 0%, #e8f4f8 100%)',
                      border: '1px solid #cfe8ef',
                      borderRadius: '10px'
                    }}
                  >
                    <div className="d-flex align-items-start" style={{ gap: '16px' }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#1199B2',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            marginBottom: '6px'
                          }}
                        >
                          Subcategory ID
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#17324d' }}>
                          {editSubcategory.subcategoryId || '-'}
                        </div>
                      </div>
                      <div
                        className="d-flex flex-column align-items-end flex-shrink-0"
                        style={{ flex: '0 0 auto', marginLeft: 'auto' }}
                      >
                        {renderSubcategoryStatus(editSubcategory.status)}
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6c757d',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px'
                      }}
                    >
                      Category
                    </label>
                    <select
                      className="form-control"
                      value={editCategoryId}
                      onChange={(e) => setEditCategoryId(e.target.value)}
                      disabled={isUpdatingSubcategory || isLoadingCategoryOptions}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #cfe8ef',
                        height: '42px',
                        padding: '0 14px',
                        fontSize: '15px',
                        lineHeight: '42px',
                        fontWeight: 600,
                        color: '#17324d',
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="">Select category</option>
                      {renderEditCategorySelectOptions()}
                    </select>
                  </div>

                  <div className="form-group mb-0">
                    <label
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#6c757d',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px'
                      }}
                    >
                      <i className="fa-solid fa-sitemap mr-2" style={{ color: '#1199B2' }} />
                      Subcategory Name
                    </label>
                    <textarea
                      rows={3}
                      className="form-control mt-2"
                      placeholder="Enter subcategory name"
                      value={editSubcategoryName}
                      onChange={(e) => setEditSubcategoryName(e.target.value.toUpperCase())}
                      disabled={isUpdatingSubcategory}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #cfe8ef',
                        padding: '10px 14px',
                        fontSize: '15px',
                        fontWeight: 600,
                        color: '#17324d',
                        resize: 'vertical',
                        minHeight: '78px',
                        textTransform: 'uppercase'
                      }}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <i className="fa-regular fa-folder-open fa-2x mb-3 d-block" />
                  Subcategory details not available.
                </div>
              )}
            </div>
            <div
              className="modal-footer border-0 justify-content-center"
              style={{ backgroundColor: '#f8fafb', padding: '14px 20px' }}
            >
              <button
                type="button"
                className="btn btn-secondary mr-2 px-4"
                data-dismiss="modal"
                onClick={handleCloseEditSubcategoryModal}
                disabled={isUpdatingSubcategory}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue px-4"
                onClick={handleUpdateSubcategory}
                disabled={
                  isUpdatingSubcategory ||
                  isLoadingEditSubcategory ||
                  !editSubcategory ||
                  !editSubcategoryName.trim() ||
                  !editCategoryId
                }
              >
                {isUpdatingSubcategory ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldeletesubcategory"
        tabIndex={-1}
        aria-labelledby="modaldeletesubcategoryLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeletesubcategoryLabel">
                Delete Subcategory
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeleting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete
                <br />
                <span className="fwt_600">{deleteSubcategoryLabel || '-'}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Subcategory
